import { Bill } from "@common/types/bill";
import { useCallback, useMemo, useState } from "react";
import { EagerLoaded } from "@common/types/base/orm";
import { Loader } from "@design-system/Loader/Loader";
import { useBills, useUpdateBill } from "@api/bill.api";
import { useScope } from "@state/scopeContext.lib";
import { useHasAnyAccrualLedger } from "@api/ledger.api";
import { Drawer } from "@design-system/Drawer/Drawer";
import { useOverlays } from "../../contexts/OverlayContext";
import { BillLineItemsView } from "./views/billLineItemsView/BillLineItemsView";
import { createDefaultBillLineItemsView } from "./views/defaultBillViews";
import { BillDetails } from "./BillDetails";
import { ManualBillEditDrawer } from "./ManualBillEditDrawer/ManualBillEditDrawer";
import styles from "./BillsDrilldownDrawer.module.scss";

interface BillLineItemsDrilldownDrawerProps {
    open: boolean;
    onClose: () => void;
    billId: string;
    entityIds: number[];
}

const BillLineItemsDrilldownDrawerContent: React.FC<
    Pick<BillLineItemsDrilldownDrawerProps, "billId" | "entityIds">
> = ({ billId, entityIds }) => {
    const [selectedBill, setSelectedBill] = useState<Bill>();
    const [editBill, setEditBill] = useState<Bill | null>(null);
    const { workspaceId } = useScope();
    const updateBillMutation = useUpdateBill();

    const handleUpdateMemo = useCallback(
        async (memo: string | null) => {
            if (!workspaceId || !selectedBill) {
                return;
            }
            await updateBillMutation.mutateAsync({
                workspaceId,
                billId: selectedBill.id,
                memo,
            });
        },
        [workspaceId, selectedBill, updateBillMutation],
    );

    const { data, isLoading } = useBills({
        filters: { ids: [billId], entityIds },
        page: 1,
        limit: 1,
    });

    const bills = data?.data ?? null;
    const hasAnyAccrualLedgers = useHasAnyAccrualLedger(entityIds);
    const hasVisibleAccrualLedgerColumn = hasAnyAccrualLedgers;

    const billLineItemsView = useMemo(
        () =>
            createDefaultBillLineItemsView({
                hasAnyAccrualLedgers: hasVisibleAccrualLedgerColumn,
            }),
        [hasVisibleAccrualLedgerColumn],
    );

    const handleBillClick = useCallback((bill?: Bill) => {
        setSelectedBill(bill);
    }, []);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className={styles.content}>
            <BillLineItemsView
                view={billLineItemsView}
                hasAnyAccrualLedgers={hasVisibleAccrualLedgerColumn}
                bills={bills}
                selectedBillId={selectedBill?.id}
                onBillClick={handleBillClick}
            />

            {selectedBill && (
                <BillDetails
                    bill={selectedBill as EagerLoaded<Bill, "lines">}
                    setBill={setSelectedBill}
                    onEdit={() => setEditBill(selectedBill)}
                    onUpdateMemo={handleUpdateMemo}
                    hideCloseButton={false}
                />
            )}

            {editBill && (
                <ManualBillEditDrawer
                    bills={[editBill]}
                    open
                    onClose={() => {
                        setEditBill(null);
                        setSelectedBill(undefined);
                    }}
                />
            )}
        </div>
    );
};

const BillLineItemsDrilldownDrawer: React.FC<
    BillLineItemsDrilldownDrawerProps
> = ({ open, onClose, billId, entityIds }) => {
    const [isOpen, setIsOpen] = useState(false);

    const shouldShowContent = isOpen || open;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            onAnimationEnd={setIsOpen}
            headerAddonLeft="Bill"
            noPadding
        >
            {shouldShowContent && (
                <BillLineItemsDrilldownDrawerContent
                    billId={billId}
                    entityIds={entityIds}
                />
            )}
        </Drawer>
    );
};

export const BillLineItemsDrilldownDrawerWrapper: React.FC = () => {
    const { getOverlayInfo, closeOverlay, overlayIsClosing } = useOverlays();
    const overlayInfo = getOverlayInfo<"billLineItemsDrilldown">(
        "billLineItemsDrilldown",
    );

    if (!overlayInfo?.data) {
        return null;
    }

    return (
        <BillLineItemsDrilldownDrawer
            open={!overlayIsClosing}
            onClose={closeOverlay}
            billId={overlayInfo.data.billId}
            entityIds={overlayInfo.data.entityIds}
        />
    );
};
